import {Injectable, OnDestroy} from '@angular/core';
import {VisualizeEngine, VisualizeEngineOptions} from './visualize.engine';
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  ReplaySubject,
  Subscription,
} from 'rxjs';
import {tap} from 'rxjs/operators';
import {calculatePositionOnCanvasAndBounds, getUsedMemoryUsage,} from "./helper";
import {ImageCache} from "./caches/image-cache";
import {CanvasCache, CanvasType} from "./caches/canvas-cache";
import {WebGLService} from "./webgl/web-gl.service";
import {ScenarioRenderer} from "./renderer/scenario-renderer";
import {
  DoorCorners,
  Point,
  ProductsAtIndex,
  ProductsMap,
  Rectangle, RenderResult,
  ScenarioImage,
  VisualizedScenarioImage
} from "./types";

@Injectable({providedIn: 'root'})
export class VisualizeEngineImpl extends VisualizeEngine implements OnDestroy {

  constructor(
    private readonly imageCache: ImageCache,
    private readonly canvasCache: CanvasCache,
    private readonly webGLService: WebGLService,
    private readonly scenarioRenderer: ScenarioRenderer,
  ) {
    super();
  }

  private engineOptions: VisualizeEngineOptions = {
    drawGlassLayer: true,
    enabledShadowProfiles: true,
    enableShadowInpainting: true,
    calculateDoorBrightness: false
  }

  canvas$ = new BehaviorSubject<HTMLCanvasElement | undefined>(undefined);
  currentCanvas$ = this.canvas$.pipe(
      filter(c => !!c && c.width !== 0 && c.height !== 0),
      map(c => c!)
  );
  currentScenarioImage$ = new ReplaySubject<ScenarioImage>(1);
  currentProducts$ = new BehaviorSubject<ProductsMap>(new Map());
  currentVisualizedScenario$ = new BehaviorSubject<VisualizedScenarioImage | undefined>(undefined);
  showDoor$ = new BehaviorSubject<boolean>(true);
  flipDoor$ = new BehaviorSubject<boolean>(false);

  alreadyInitialized = false;

  private visualizationSub: Subscription | undefined;

  throwEngineError(error: Error): void {
    console.groupEnd()
    console.groupEnd()
    console.groupEnd()
    console.groupEnd()
    console.groupEnd()
    console.groupEnd()
    this.onEngineError$.next(error);
    this.cleanAll()
    this.isVisualizationInProgress$.next(false);
  }

  public prepare(): void {
    if (!this.alreadyInitialized) {
      this.alreadyInitialized = true;

      // if canvas changes or current scenario, update VisualizedScenario Bounds and reinitialize visualizeEngine
      combineLatest([
        this.currentCanvas$,
        this.currentScenarioImage$
      ]).subscribe({
        next: ([canvas, scenario]) => {
          this.imageCache.get(scenario.image).then(img => {
            const {positionInCanvas, boundsOnScreen, newImageSize} = calculatePositionOnCanvasAndBounds({
              img,
              canvas,
              canvasBounds: this.getCanvasBounds()
            })

            const visualizedScenario = {
              image: img,
              size: newImageSize,
              scale: newImageSize.scale,
              lightPosition: scenario.lightPosition,
              positionInCanvas,
              boundsOnScreen,
              corners: scenario.corners,
              lightSources: scenario.lightSources,
              scenarioImage: scenario
            };

            // clean up current image
            // this.currentVisualizedScenario$.getValue()?.image.remove();
            this.currentVisualizedScenario$.next(visualizedScenario);
            this.currentScenario$.next(visualizedScenario);
          }).catch(this.throwEngineError.bind(this));
        },
        error: this.throwEngineError.bind(this)
      });

      this.visualizationSub = combineLatest([
        this.currentCanvas$.pipe(distinctUntilChanged()),
        this.currentScenario$.pipe(distinctUntilChanged()),
        this.currentProducts$.pipe(distinctUntilChanged()),
        this.flipDoor$.pipe(distinctUntilChanged()),
        this.showDoor$.pipe(distinctUntilChanged()),
      ])
        .pipe(
          tap(() => this.isVisualizationInProgress$.next(true)),
          concatMap(async ([canvas, scenario, products, flipDoor, showDoor]) => {
            return this.visualizeScenario({
              canvas,
              scenario,
              products,
              flipDoor,
              showDoor
            })
          })
        )
          .subscribe({
            next: (renderResult) => {
              this.renderResult$.next(renderResult);  // Expose renderResult via BehaviorSubject
            },
            error: this.throwEngineError.bind(this)
          });
    }
  }

  private async visualizeScenario(
    {canvas, scenario, products, flipDoor, showDoor}: {
      canvas: HTMLCanvasElement,
      scenario: VisualizedScenarioImage,
      products: ProductsMap,
      flipDoor: boolean,
      showDoor: boolean
    }): Promise<RenderResult | undefined> {
    console.time('⏱️')
    console.groupCollapsed('🎨 Visualizing scenario')

    let renderResult: RenderResult | undefined = undefined;
    try {
      this.isVisualizationInProgress$.next(true);

      renderResult = await this.scenarioRenderer.renderScenario({
        canvas,
        scenario: {
          corners: scenario.corners,
          image: scenario.image,
          shadowImageUrl: scenario.scenarioImage.shadowImage?.url,
          size: scenario.size,
          lightPosition: scenario.lightPosition,
          positionInCanvas: scenario.positionInCanvas
        },
        products
      }, {
        flipDoor,
        showDoor,
        ...this.engineOptions,
        enabledShadowProfiles: this.engineOptions.enabledShadowProfiles,
        enableShadowInpainting: this.engineOptions.enableShadowInpainting,
        drawGlassLayer: this.engineOptions.drawGlassLayer,
        compositionOperator: this.engineOptions.compositionOperator,
        shadowInpaintingAlpha: this.engineOptions.shadowInpaintingAlpha
      })
    } catch (e) {
      console.error('some error during drawing', e);
      this.throwEngineError(e as Error);
    } finally {
      this.isVisualizationInProgress$.next(false);
      console.groupEnd()
      console.timeEnd('⏱️')
      console.log('🧠 Memory usage:', getUsedMemoryUsage() + '%')
      console.log('---------------------------------')
    }

    return renderResult
  }

  init(options: VisualizeEngineOptions): void {
    this.engineOptions = options
    this.cleanAll();
    this.prepare();
  }

  updateCanvas(canvas: HTMLCanvasElement) {
    this.canvas$.next(canvas);
  }

  drawProducts(products: ProductsMap): void {
    this.currentProducts$.next(this.deepCloneProductsMap(products))
  }

  private deepCloneProductsMap(originalMap: ProductsMap): ProductsMap {
    const newMap = new Map<number, ProductsAtIndex>();

    originalMap.forEach((value, key) => {
      const clonedValue: ProductsAtIndex = {
        index: value.index,
        door: value.door ? {...value.door} : undefined,
        products: value.products ? value.products.map(product => ({...product})) : undefined
      };
      newMap.set(key, clonedValue);
    });

    return newMap;
  }

  drawScenario(scenario: ScenarioImage): void {
    this.currentScenarioImage$.next(scenario);
  }

  getCurrentScenarioValue(): VisualizedScenarioImage | undefined {
    return this.currentVisualizedScenario$.getValue();
  }

  convertScenarioPositionToScreenPosition(corners: Array<DoorCorners>): Array<DoorCorners> {
    const scenario = this.currentVisualizedScenario$.getValue();
    if (!scenario) {
      throw new Error('visualize engine not ready yet, please choose an user scenario');
    }

    const bounds = this.getCanvasBounds();

    return corners.map(c => {
      return {
        ...c,
        topLeft: this.convertPositionToScreenPosition({point: c.topLeft, scenario, bounds}),
        topRight: this.convertPositionToScreenPosition({point: c.topRight, scenario, bounds}),
        bottomRight: this.convertPositionToScreenPosition({point: c.bottomRight, scenario, bounds}),
        bottomLeft: this.convertPositionToScreenPosition({point: c.bottomLeft, scenario, bounds}),
        topLeftBottom: c.topLeftBottom && this.convertPositionToScreenPosition({
          point: c.topLeftBottom,
          scenario,
          bounds
        }),
        topLeftRight: c.topLeftRight && this.convertPositionToScreenPosition({point: c.topLeftRight, scenario, bounds}),
        topRightBottom: c.topRightBottom && this.convertPositionToScreenPosition({
          point: c.topRightBottom,
          scenario,
          bounds
        }),
        topRightLeft: c.topRightLeft && this.convertPositionToScreenPosition({point: c.topRightLeft, scenario, bounds}),
      };
    })
  }

  private convertPositionToScreenPosition({point, scenario, bounds}: {
    point: Point,
    scenario: VisualizedScenarioImage,
    bounds: Rectangle
  }): Point {
    return {
      x: bounds.x + scenario.positionInCanvas.x + (point.x * scenario.scale),
      y: bounds.y + scenario.positionInCanvas.y + (point.y * scenario.scale)
    }
  }

  getCanvasBounds(): Rectangle {
    if (!this.canvas$.getValue()) {
      return new Rectangle(0, 0, 0, 0);
    }

    const canvasRect = this.canvas$.getValue()!.getBoundingClientRect();

    return new Rectangle(canvasRect.x, canvasRect.y, canvasRect.width, canvasRect.height);
  }

  convertScreenPositionToScenarioPosition(corners: Array<DoorCorners>): Array<DoorCorners> {
    const scenario = this.currentVisualizedScenario$.getValue();
    if (!scenario) {
      throw new Error('visualize engine not ready yet, please choose an user scenario');
    }

    const bounds = scenario.boundsOnScreen;

    return corners.map(c => {
      return {
        index: c.index,
        doorType: c.doorType,
        hasClippedCorners: c.hasClippedCorners,
        topLeft: this.convertPositionToScenarioPosition({point: c.topLeft, scenario, bounds}),
        topRight: this.convertPositionToScenarioPosition({point: c.topRight, scenario, bounds}),
        bottomRight: this.convertPositionToScenarioPosition({point: c.bottomRight, scenario, bounds}),
        bottomLeft: this.convertPositionToScenarioPosition({point: c.bottomLeft, scenario, bounds}),
        topLeftBottom: c.topLeftBottom && this.convertPositionToScenarioPosition({
          point: c.topLeftBottom,
          scenario,
          bounds
        }),
        topLeftRight: c.topLeftRight && this.convertPositionToScenarioPosition({
          point: c.topLeftRight,
          scenario,
          bounds
        }),
        topRightBottom: c.topRightBottom && this.convertPositionToScenarioPosition({
          point: c.topRightBottom,
          scenario,
          bounds
        }),
        topRightLeft: c.topRightLeft && this.convertPositionToScenarioPosition({
          point: c.topRightLeft,
          scenario,
          bounds
        }),
      };
    })
  }

  private convertPositionToScenarioPosition({point, scenario, bounds}: {
    point: Point,
    scenario: VisualizedScenarioImage,
    bounds: Rectangle
  }): Point {
    return {
      x: (point.x - bounds.x) / scenario!.scale,
      y: (point.y - bounds.y) / scenario!.scale
    }
  }

  flipDoor(doorCornerId: number): void {
    this.flipDoor$.next(!this.flipDoor$.getValue());
  }

  public setRenderDoor(shouldRenderDoor: boolean) {
    this.showDoor$.next(shouldRenderDoor);
  }

  async getVisualizationInOriginalSize(): Promise<HTMLCanvasElement | undefined> {
    const currentScenario = this.currentVisualizedScenario$.getValue()
    if (!currentScenario) {
      throw new Error('No current scenario found')
    }

    const { canvas } = this.canvasCache.get(CanvasType.DOWNLOAD, currentScenario.image)

    await this.visualizeScenario({
      canvas,
      scenario: {
        ...currentScenario,
        positionInCanvas: {
          x: 0,
          y: 0
        },
        size: currentScenario.image,

      },
      products: this.currentProducts$.getValue(),
      flipDoor: this.flipDoor$.getValue(),
      showDoor: true
    })

    return canvas;
  }

  private cleanAll() {
    this.imageCache.clear();
    this.canvasCache.clear();
    this.webGLService.clear();
  }

  ngOnDestroy(): void {
    this.visualizationSub?.unsubscribe();
    this.cleanAll();
  }
}
